import React, { useEffect, useState } from 'react';
import { Table, Modal, Form, Button, Badge, InputGroup, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import client from '../api/client';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { fmtMoney, fmtDate } from '../utils/format';

const empty = {
  name: '',
  code: '',
  city: '',
  location: '',
  description: '',
  actualRatePerSqft: 0,
  bookingRatePerSqft: 0,
  totalUnits: 0,
  status: 'under_construction',
};

const Projects = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState('');

  const canEdit = ['admin', 'builder'].includes(user.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/projects', { params: { q } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setShow(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...empty, ...it });
    setShow(true);
  };
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await client.put(`/projects/${editing._id}`, form);
        toast.success('Project updated');
      } else {
        await client.post('/projects', form);
        toast.success('Project created');
      }
      setShow(false);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.message || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this project?')) return;
    await client.delete(`/projects/${id}`);
    toast.info('Project deactivated');
    load();
  };

  return (
    <div>
      <PageHeader
        title="Projects"
        subtitle="Real estate projects / inventory"
        actionLabel={canEdit ? 'New Project' : null}
        onAction={openNew}
      />

      <InputGroup className="mb-3" style={{ maxWidth: 360 }}>
        <Form.Control placeholder="Search by name, code, city" value={q} onChange={(e) => setQ(e.target.value)} />
        <Button onClick={load}><i className="bi bi-search"></i></Button>
      </InputGroup>

      {loading ? <Spinner animation="border" /> : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Name</th><th>Code</th><th>City</th><th>Booking Rate/sqft</th>
              <th>Actual Rate/sqft</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-4">No projects</td></tr>
            )}
            {items.map((p) => (
              <tr key={p._id}>
                <td><strong>{p.name}</strong><br /><small className="text-muted">{p.location}</small></td>
                <td>{p.code || '-'}</td>
                <td>{p.city || '-'}</td>
                <td>{fmtMoney(p.bookingRatePerSqft)}</td>
                <td>{fmtMoney(p.actualRatePerSqft)}</td>
                <td><Badge bg="info">{p.status}</Badge></td>
                <td className="text-end">
                  {canEdit && (
                    <>
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(p)}>
                        <i className="bi bi-pencil"></i>
                      </Button>{' '}
                      {user.role === 'admin' && (
                        <Button size="sm" variant="outline-danger" onClick={() => remove(p._id)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title>{editing ? 'Edit Project' : 'New Project'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6"><Form.Label>Name *</Form.Label>
                <Form.Control required value={form.name} onChange={(e) => onChange('name', e.target.value)} /></div>
              <div className="col-md-3"><Form.Label>Code</Form.Label>
                <Form.Control value={form.code} onChange={(e) => onChange('code', e.target.value)} /></div>
              <div className="col-md-3"><Form.Label>Status</Form.Label>
                <Form.Select value={form.status} onChange={(e) => onChange('status', e.target.value)}>
                  <option value="planned">Planned</option>
                  <option value="under_construction">Under Construction</option>
                  <option value="ready">Ready</option>
                  <option value="completed">Completed</option>
                </Form.Select></div>
              <div className="col-md-6"><Form.Label>City</Form.Label>
                <Form.Control value={form.city} onChange={(e) => onChange('city', e.target.value)} /></div>
              <div className="col-md-6"><Form.Label>Location</Form.Label>
                <Form.Control value={form.location} onChange={(e) => onChange('location', e.target.value)} /></div>
              <div className="col-md-4"><Form.Label>Booking Rate (₹/sqft)</Form.Label>
                <Form.Control type="number" value={form.bookingRatePerSqft} onChange={(e) => onChange('bookingRatePerSqft', Number(e.target.value))} /></div>
              <div className="col-md-4"><Form.Label>Actual Rate (₹/sqft)</Form.Label>
                <Form.Control type="number" value={form.actualRatePerSqft} onChange={(e) => onChange('actualRatePerSqft', Number(e.target.value))} /></div>
              <div className="col-md-4"><Form.Label>Total Units</Form.Label>
                <Form.Control type="number" value={form.totalUnits} onChange={(e) => onChange('totalUnits', Number(e.target.value))} /></div>
              <div className="col-12"><Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => onChange('description', e.target.value)} /></div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
            <Button type="submit" variant="primary">{editing ? 'Save' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Projects;
